import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './ProjectCard.css';
import { useProject } from '../../../../contexts/ProjectContext';
import { getVirtualTourUrl, getVirtualTourCode, getProjectVirtualTours, getVirtualTourUrlByCode } from '../../../../utils/virtualTourMapping';
import { useEC2Loading } from '../../../../contexts/EC2LoadingContext';
import { useEC2InstanceStatus } from '../../../../contexts/EC2InstanceStatusContext';
import EC2LoadingBar from './EC2LoadingBar';
import ManageProjectUsersModal from './ManageProjectUsersModal';
import Launch3DViewerModal from './Launch3DViewerModal';
import viewerPresenceService from '../../../../services/viewerPresenceService';

const ProjectCard = ({ project, onProjectSelect, onViewProject, formatDate, user, customRedirectPath }) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [showEC2Loading, setShowEC2Loading] = useState(false);
  const [menuOpensUpward, setMenuOpensUpward] = useState(false);
  const [showManageUsersModal, setShowManageUsersModal] = useState(false);
  const [activeViewers, setActiveViewers] = useState([]);
  const [showViewersTooltip, setShowViewersTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const menuRef = useRef(null);
  const viewersIconRef = useRef(null);
  const tooltipRef = useRef(null);
  const { stopLoading, isProjectLoading, startLoading, getLoadingProject } = useEC2Loading();
  
  // Check if this project is loading from global context
  const isGloballyLoading = isProjectLoading(project.Id);
  const loadingState = getLoadingProject(project.Id);
  const loadingProgress = loadingState?.progress || 0;
  const isInTransition = isGloballyLoading && loadingProgress < 100;
  
  // Get EC2 instance status
  const { getInstanceStatus, isInstanceRunning, isInstanceStopped, hasInstanceError } = useEC2InstanceStatus();
  const instanceStatus = getInstanceStatus(String(project.Id));
  const isRunning = isInstanceRunning(String(project.Id));
  const isStopped = isInstanceStopped(String(project.Id));
  const hasError = hasInstanceError(String(project.Id));
  
  // Check if instance is stopping or shutting down
  const isStopping = instanceStatus?.state === 'stopping' || instanceStatus?.state === 'shutting-down';
  
  // Use project context utilities
  const { isProjectExpired, canViewProject, canAccessProjectFiles, formatFileSize } = useProject();

  const isExpired = isProjectExpired(project);
  const canView = canViewProject(project);
  const canAccessFiles = canAccessProjectFiles(project);

  const handleImageError = () => {
    console.warn(`⚠️ ProjectCard: Image failed to load for project ${project.Id}, retry count: ${retryCount}`);
    
    // Retry up to 2 times with exponential backoff
    if (retryCount < 2) {
      const delay = (retryCount + 1) * 1000; // 1s, 2s
      console.log(`🔄 Retrying image load in ${delay}ms...`);
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setImageError(false);
        setImageLoading(true);
      }, delay);
    } else {
      console.error(`❌ ProjectCard: Image failed after ${retryCount + 1} attempts for project ${project.Id}`);
      setImageError(true);
      setImageLoading(false);
    }
  };

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
    setRetryCount(0); // Reset retry count on successful load
  };

  const getProjectInitial = () => {
    return project.Name ? project.Name.charAt(0).toUpperCase() : 'P';
  };

  const getGradientClass = () => {
    const firstLetter = getProjectInitial();
    return `gradient-${firstLetter}`;
  };

  // Reset image error and loading state when project changes
  useEffect(() => {
    setImageError(false);
    setImageLoading(true);
    setRetryCount(0);
  }, [project.Id, project.ThumbnailURL]);

  // Memoize thumbnail URL to prevent regenerating on every render
  // Only regenerate when project.ThumbnailURL changes, project.Id changes, or retry count changes
  const thumbnailUrl = useMemo(() => {
    // Don't return null based on imageError here - let the retry logic handle it
    // Check if ThumbnailURL is null, undefined, or empty string
    if (!project.ThumbnailURL || project.ThumbnailURL === 'null' || project.ThumbnailURL.trim() === '') {
      return null; // Will show fallback
    }
    
    // Use cache-busting with timestamp and retry count to force fresh loads
    // This ensures the browser doesn't use a stale cached version
    const cacheBuster = retryCount > 0 
      ? `&retry=${retryCount}&t=${Date.now()}` 
      : `&t=${Date.now()}`;
    
    return `${project.ThumbnailURL}?projectId=${project.Id}${cacheBuster}`;
  }, [project.ThumbnailURL, project.Id, retryCount]);


  const handleMenuToggle = (e) => {
    e.stopPropagation();
    
    if (!showMenu) {
      // Check if there's enough space below for the menu
      if (menuRef.current) {
        const buttonRect = menuRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        const estimatedMenuHeight = 300; // Approximate height of the menu
        
        // If not enough space below, open upward
        setMenuOpensUpward(spaceBelow < estimatedMenuHeight);
      }
    }
    
    setShowMenu(!showMenu);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
      // Close viewers tooltip when clicking outside
      if (viewersIconRef.current && !viewersIconRef.current.contains(event.target) &&
          tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowViewersTooltip(false);
      }
    };

    if (showMenu || showViewersTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu, showViewersTooltip]);

  // Poll for active viewers every 12 seconds
  useEffect(() => {
    if (!project || !project.Id) {
      return;
    }

    const projectId = project.Id;

    // Fetch immediately
    const fetchViewers = async () => {
      try {
        const viewers = await viewerPresenceService.getActiveViewers(projectId);
        setActiveViewers(viewers || []);
      } catch (error) {
        console.warn(`❌ ProjectCard: Failed to fetch active viewers for project ${projectId}:`, error);
        setActiveViewers([]);
      }
    };

    fetchViewers();

    // Set up polling interval (every 12 seconds)
    const pollInterval = setInterval(fetchViewers, 12000);

    return () => {
      clearInterval(pollInterval);
    };
  }, [project]);

  const handleMenuAction = (action, e) => {
    e.stopPropagation();
    setShowMenu(false);
    
    switch (action) {
      case 'settings':
        // TODO: Navigate to project settings
        console.log('Project settings:', project.Id);
        break;
      case 'share':
        // TODO: Navigate to project sharing
        console.log('Share project:', project.Id);
        break;
      case 'rename':
        // TODO: Show rename dialog
        console.log('Rename project:', project.Id);
        break;
      case 'download':
        // TODO: Download project shortcut
        window.location.href = `/api/shortcut/project?projectId=${project.Id}`;
        break;
      case 'downloadVTour':
        // TODO: Download virtual tour shortcut
        window.location.href = `/api/shortcut/vtour?projectId=${project.Id}`;
        break;
      case 'leave':
        // TODO: Show leave project dialog
        console.log('Leave project:', project.Id);
        break;
      case 'copy':
        // TODO: Copy project model
        console.log('Copy project model:', project.Id);
        break;
      case 'paste':
        // TODO: Paste project model
        console.log('Paste project model:', project.Id);
        break;
      case 'manageUsers':
        setShowManageUsersModal(true);
        break;
      default:
        break;
    }
  };

  return (
    <div className={`project-card gallery-view ${isExpired ? 'expired' : ''}`}>
      {/* EC2 Loading Bar - Show if globally loading */}
      {isGloballyLoading && (
        <EC2LoadingBar
          projectId={project.Id}
          onComplete={() => {
            // URL opening is now handled inside EC2LoadingBar after 1.5 minute delay
            console.log(`✅ EC2 instance ready for project ${project.Id}`);
          }}
          onError={(error) => {
            console.error('EC2 loading bar error:', error);
            console.error('Error details:', {
              message: error.message,
              projectId: error.projectId,
              instanceId: error.instanceId,
              instanceName: error.instanceName,
              backendResponse: error.backendResponse,
              troubleshooting: error.troubleshooting
            });
            stopLoading(project.Id);
            
            // Extract the most helpful error message
            let displayMessage = error.message || 'Unknown error';
            
            // If we have backend response with detailed error, use that
            if (error.backendResponse?.error) {
              const backendError = error.backendResponse.error;
              // Check for specific AWS errors and provide user-friendly messages
              if (backendError.includes('VcpuLimitExceeded')) {
                displayMessage = 'AWS vCPU limit exceeded. You have reached your EC2 instance vCPU limit. Please contact AWS support to request an increase, or stop other running instances.';
              } else if (backendError.includes('InsufficientInstanceCapacity')) {
                displayMessage = 'Insufficient instance capacity. The requested instance type is not available in this region. Please try again later.';
              } else if (backendError.includes('InstanceLimitExceeded')) {
                displayMessage = 'Instance limit exceeded. You have reached the maximum number of running instances. Please stop other instances before starting a new one.';
              } else {
                // Use a cleaned version of the backend error
                displayMessage = backendError.replace(/An error occurred \([^)]+\) when calling the [^:]+: /, '');
              }
            }
            
            // Show error message with troubleshooting tips if available
            let errorMsg = `Failed to start 3D Viewer instance:\n\n${displayMessage}`;
            
            // Check all possible locations for troubleshooting information
            const troubleshooting = error.troubleshooting || 
                                  error.backendResponse?.troubleshooting || 
                                  error.response?.data?.troubleshooting;
            
            if (troubleshooting) {
              const tips = Array.isArray(troubleshooting) ? troubleshooting : [troubleshooting];
              errorMsg += '\n\nTroubleshooting:\n' + tips.map((tip, idx) => `${idx + 1}. ${tip}`).join('\n');
            }
            
            // Add instance details if available
            if (error.instanceId || error.instanceName) {
              errorMsg += `\n\nInstance: ${error.instanceName || 'Unknown'} (${error.instanceId || 'Unknown ID'})`;
            }
            
            // Add link to AWS support for vCPU limit issues
            if (error.backendResponse?.error?.includes('VcpuLimitExceeded')) {
              errorMsg += '\n\nTo request a vCPU limit increase, visit: https://aws.amazon.com/contact-us/ec2-request';
            }
            
            alert(errorMsg);
          }}
          onStopping={() => {
            console.log('EC2 instance is stopping, showing message and hiding loading bar');
            stopLoading(project.Id);
            // Show message to user about stopping state
            alert('3D Viewer is shutting down. Please wait a few minutes for it to fully stop, then try again.');
          }}
          onSuccess={async () => {
            // Navigate to the project viewer page when loading completes
            if (!isExpired && canView) {
              console.log(`🎯 Navigating to project viewer for project ${project.Id}...`);
              
              try {
                // Register overlay click with backend (await to ensure it completes)
                // This tracks the user as active in the viewer
                await viewerPresenceService.registerOverlayClick(project.Id);
                console.log(`✅ Overlay click registered for project ${project.Id}`);
              } catch (error) {
                console.warn(`⚠️ Failed to register overlay click for project ${project.Id}:`, error);
                // Continue with navigation even if registration fails
              }
              
              // Navigate to the viewer page (without fullscreen parameter)
              console.log(`🚀 Navigating to /projects/${project.Id}/view`);
              navigate(`/projects/${project.Id}/view`);
            }
          }}
        />
      )}
      
      <div className={`project-thumbnail ${getGradientClass()}`}>
        {/* Active Viewers Icon - Only show when there are active viewers */}
        {activeViewers.length > 0 && (
          <div 
            className="active-viewers-icon-container thumbnail-position"
            ref={viewersIconRef}
            onMouseEnter={(e) => {
              if (viewersIconRef.current) {
                const rect = viewersIconRef.current.getBoundingClientRect();
                setTooltipPosition({
                  top: rect.bottom + window.scrollY + 8,
                  left: rect.left + window.scrollX
                });
              }
              setShowViewersTooltip(true);
            }}
            onMouseLeave={() => {
              // Delay to allow mouse to move to tooltip
              setTimeout(() => {
                if (tooltipRef.current && !tooltipRef.current.matches(':hover')) {
                  setShowViewersTooltip(false);
                }
              }, 100);
            }}
          >
            <button 
              className="active-viewers-icon"
              title={`${activeViewers.length} user${activeViewers.length > 1 ? 's' : ''} viewing`}
              onClick={(e) => {
                e.stopPropagation();
                if (viewersIconRef.current) {
                  const rect = viewersIconRef.current.getBoundingClientRect();
                  setTooltipPosition({
                    top: rect.bottom + window.scrollY + 8,
                    left: rect.left + window.scrollX
                  });
                }
                setShowViewersTooltip(!showViewersTooltip);
              }}
            >
              <i className="fas fa-user"></i>
              <span className="viewers-count">{activeViewers.length}</span>
            </button>
            
            {/* Tooltip showing all viewer emails */}
            {showViewersTooltip && typeof document !== 'undefined' && createPortal(
              <div 
                className="viewers-tooltip"
                ref={tooltipRef}
                style={{
                  top: `${tooltipPosition.top}px`,
                  left: `${tooltipPosition.left}px`
                }}
                onMouseEnter={() => setShowViewersTooltip(true)}
                onMouseLeave={() => setShowViewersTooltip(false)}
              >
                <div className="viewers-tooltip-header">
                  <i className="fas fa-users"></i>
                  <span>{activeViewers.length} Active Viewer{activeViewers.length > 1 ? 's' : ''}</span>
                </div>
                <div className="viewers-tooltip-list">
                  {activeViewers.map((email, index) => (
                    <div key={index} className="viewers-tooltip-item">
                      <i className="fas fa-user-circle"></i>
                      <span>{email}</span>
                    </div>
                  ))}
                </div>
              </div>,
              document.body
            )}
          </div>
        )}
        
        {thumbnailUrl && !imageError ? (
          <>
            <img 
              src={thumbnailUrl} 
              alt={project.Name}
              onError={handleImageError}
              onLoad={handleImageLoad}
              style={{ 
                display: imageLoading ? 'none' : 'block',
                opacity: imageLoading ? 0 : 1,
                transition: 'opacity 0.3s ease-in-out'
              }}
              loading="eager"
              decoding="async"
            />
            {imageLoading && (
              <div className="project-thumbnail-loading">
                <div className="project-initial-large">{getProjectInitial()}</div>
              </div>
            )}
          </>
        ) : (
          <div className="project-initial-large">{getProjectInitial()}</div>
        )}
        <div 
          className="project-3d-overlay"
          onClick={async (e) => {
            console.log(`🎯 3D Overlay clicked for project ${project.Id}!`);
            e.stopPropagation();
            if (!isExpired && canView) {
              // Register overlay click with backend (required for showing user as active)
              try {
                await viewerPresenceService.registerOverlayClick(project.Id);
                console.log(`✅ Overlay click registered for project ${project.Id}`);
              } catch (error) {
                console.warn(`⚠️ Failed to register overlay click for project ${project.Id}:`, error);
                // Continue with navigation even if registration fails
              }
              
              // Check instance state before proceeding
              // If instance is stopping, show message and don't start loading
              if (isStopping && !isGloballyLoading) {
                console.log(`⚠️ Instance is stopping, cannot start for project ${project.Id}`);
                alert('3D Viewer is currently shutting down. Please wait a few minutes for it to fully stop, then try again.');
                return; // Don't navigate or show modal
              }
              
              // Check if instance is stopped - show launch modal if so
              // But if it's loading (from broadcast or local launch), don't show modal
              if (isStopped && instanceStatus && !isGloballyLoading && !isStopping) {
                console.log(`⚠️ Instance is stopped, showing launch modal for project ${project.Id}`);
                setShowLaunchModal(true);
              } else if (isGloballyLoading) {
                // Instance is loading - navigate anyway (progress bar will show)
                console.log(`🚀 Instance is loading, navigating to /projects/${project.Id}/view`);
                navigate(`/projects/${project.Id}/view`);
              } else {
                // Navigate to the project viewer route
                console.log(`🚀 Navigating to /projects/${project.Id}/view`);
                navigate(`/projects/${project.Id}/view`);
              }
            }
          }}
          style={{ 
            cursor: (!isExpired && canView) ? 'pointer' : 'default',
            pointerEvents: (!isExpired && canView) ? 'auto' : 'none'
          }}
        >
          {/* Open in new tab button */}
          {!isExpired && canView && (
            <button
              className="project-3d-open-new-tab"
              onClick={async (e) => {
                e.stopPropagation();
                console.log(`🔗 Opening project ${project.Id} in new tab`);
                
                // Register overlay click with backend (required for showing user as active)
                try {
                  await viewerPresenceService.registerOverlayClick(project.Id);
                  console.log(`✅ Overlay click registered for project ${project.Id} (new tab)`);
                } catch (error) {
                  console.warn(`⚠️ Failed to register overlay click for project ${project.Id}:`, error);
                  // Continue with opening tab even if registration fails
                }
                
                window.open(`/projects/${project.Id}/view`, '_blank', 'noopener,noreferrer');
              }}
              title="Open in new tab"
              aria-label="Open in new tab"
            >
              <i className="fas fa-external-link-alt"></i>
            </button>
          )}
          <span className="project-3d-icon">explore</span>
          <span className="project-3d-text">3D View</span>
        </div>
        
        {isExpired && (
          <div className="expired-overlay">
            <i className="fas fa-lock"></i>
            <h4>Project Expired</h4>
            <p>Contact <a href="mailto:sales@rdvsystems.com">sales@rdvsystems.com</a></p>
            <button 
              className="leave-btn"
              onClick={(e) => handleMenuAction('leave', e)}
            >
              Leave Project
            </button>
          </div>
        )}
        
        <div className={`project-card-menu ${showMenu ? 'show' : ''}`} ref={menuRef}>
          <button 
            className="project-menu-trigger" 
            onClick={handleMenuToggle}
            disabled={isExpired}
          >
            <div className="hamburger-icon">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>
          
          {showMenu && (
            <div className={`project-menu-dropdown ${menuOpensUpward ? 'open-upward' : ''}`}>
              {isExpired ? (
                <div className="expired-menu-content">
                  <h4>Project Expired</h4>
                  <p>Contact <a href="mailto:sales@rdvsystems.com">sales@rdvsystems.com</a></p>
                  <button 
                    className="leave-btn"
                    onClick={(e) => handleMenuAction('leave', e)}
                  >
                    Leave Project
                  </button>
                </div>
              ) : (
                <>
                  <button onClick={(e) => handleMenuAction('view', e)}>
                    <i className="fas fa-eye"></i>
                    View Project
                  </button>
                  <button onClick={(e) => handleMenuAction('files', e)}>
                    <i className="fas fa-folder-open"></i>
                    Project Files
                  </button>
                  <button onClick={(e) => handleMenuAction('manageUsers', e)}>
                    <i className="fas fa-users-cog"></i>
                    Manage Project Users
                  </button>
                  <button onClick={(e) => handleMenuAction('vtour', e)}>
                    <i className="fas fa-vr-cardboard"></i>
                    Virtual Tour
                  </button>
                  <button onClick={(e) => handleMenuAction('leave', e)}>
                    <i className="fas fa-sign-out-alt"></i>
                    Leave Project
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      
      <div className="project-info">
        <div className="project-title-container">
          <h3 className="project-title">{project.Name}</h3>
          {/* EC2 Instance Status Indicator - Prioritize loading state over EC2 instance status */}
          {(instanceStatus || isGloballyLoading) && (
            <div 
              className={`ec2-status-indicator ${
                hasError 
                  ? 'error' 
                  : isGloballyLoading && loadingProgress < 100
                    ? 'transition'  // Show orange when loading and progress < 100%
                    : isGloballyLoading && loadingProgress >= 100
                      ? 'running'  // Show green when loading and progress >= 100%
                      : isRunning && !isGloballyLoading
                        ? 'running'  // Show green when running and not loading
                        : isStopped 
                          ? 'stopped' 
                          : 'unknown'
              }`}
              title={
                hasError 
                  ? (instanceStatus?.message || 'Can\'t find instance match to project') 
                  : isGloballyLoading && loadingProgress < 100
                    ? `3D viewer starting... (${loadingProgress}%)`
                    : isGloballyLoading && loadingProgress >= 100
                      ? `3D viewer ready (${loadingProgress}%)`
                      : isRunning
                        ? `3D viewer ${instanceStatus?.state || 'running'}`
                        : `3D viewer ${instanceStatus?.state || 'unknown'}`
              }
            >
              {hasError ? (
                <i className="fas fa-exclamation-triangle ec2-status-warning-icon"></i>
              ) : (
                <span className="ec2-status-dot"></span>
              )}
            </div>
          )}
        </div>
        
        <div className="project-meta">
          <div className="project-meta-left">
            <span>Last Modified: {formatDate(project.Modified)}</span>
            {project.UserRoleId < 2 && (
              <span className="expiry">Exp. Date: {project.Expiration ? (project.Expiration instanceof Date ? project.Expiration.toLocaleDateString() : new Date(project.Expiration).toLocaleDateString()) : 'N/A'}</span>
            )}
          </div>
          
          <div className="project-meta-bottom">
            <span className="role">{project.UserRole}</span>
            
            <div className="project-actions">
              {canAccessFiles && (
                <button 
                  className="action-link files"
                  onClick={() => onProjectSelect(project)}
                  disabled={isExpired}
                >
                  <i className="fas fa-folder-open"></i>
                  Project Files
                </button>
              )}
              
              {/* Always render VirtualTourButton - it will decide internally if it should show */}
              <VirtualTourButton 
                project={project} 
                isExpired={isExpired}
              />
            </div>
          </div>
        </div>
      </div>
      
      
      <ManageProjectUsersModal
        projectId={project.Id}
        projectName={project.Name}
        isOpen={showManageUsersModal}
        onClose={() => setShowManageUsersModal(false)}
      />
      
      <Launch3DViewerModal
        projectId={project.Id}
        projectName={project.Name}
        isOpen={showLaunchModal}
        onClose={() => setShowLaunchModal(false)}
        onLaunchSuccess={async () => {
          // Check instance state before starting
          const currentStatus = getInstanceStatus(String(project.Id));
          const currentIsStopping = currentStatus?.state === 'stopping' || currentStatus?.state === 'shutting-down';
          
          if (currentIsStopping) {
            console.log(`⚠️ Cannot start loading - instance is stopping for project ${project.Id}`);
            alert('3D Viewer is currently shutting down. Please wait a few minutes for it to fully stop, then try again.');
            setShowLaunchModal(false);
            return;
          }
          
          // Start the loading bar which will handle the instance launch
          console.log(`🚀 Starting EC2 loading for project ${project.Id}`);
          startLoading(project.Id, project.Name);
        }}
      />
    </div>
  );
};

// Virtual Tour Dropdown Component
const VirtualTourButton = ({ project, isExpired }) => {
  const [virtualTours, setVirtualTours] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);
  const [dropdownStyles, setDropdownStyles] = useState({});

  // Load virtual tours on component mount with retry logic
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const MAX_RETRIES = 2;
    const RETRY_DELAY = 1000; // 1 second
    
    const loadVirtualTours = async (attempt = 0) => {
      if (!isMounted) return;
      
      setIsLoading(true);
      try {
        const tours = await getProjectVirtualTours(project.Id);
        
        if (!isMounted) return;
        
        // Ensure we got valid data
        if (Array.isArray(tours)) {
          setVirtualTours(tours);
        } else {
          console.warn(`⚠️ VirtualTourButton: Invalid tours data for project ${project.Id}:`, tours);
          setVirtualTours([]);
        }
      } catch (error) {
        console.error(`❌ VirtualTourButton: Failed to load tours for project ${project.Id} (attempt ${attempt + 1}):`, error);
        
        // Retry if we haven't exceeded max retries
        if (attempt < MAX_RETRIES && isMounted) {
          retryCount++;
          console.log(`🔄 VirtualTourButton: Retrying in ${RETRY_DELAY}ms... (${retryCount}/${MAX_RETRIES})`);
          setTimeout(() => {
            if (isMounted) {
              loadVirtualTours(attempt + 1);
            }
          }, RETRY_DELAY);
          return; // Don't set loading to false yet
        }
        
        // If all retries failed, set empty array
        if (isMounted) {
          setVirtualTours([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    loadVirtualTours();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [project.Id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideToggle = dropdownRef.current?.contains(event.target);
      const clickedInsideMenu = menuRef.current?.contains(event.target);
      if (!clickedInsideToggle && !clickedInsideMenu) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleVirtualTourClick = (e, tour) => {
    e.stopPropagation();
    const virtualTourUrl = getVirtualTourUrlByCode(tour.code);
    console.log('🔗 Opening virtual tour URL:', virtualTourUrl, 'for tour:', tour.vtour_name);
    window.open(virtualTourUrl, '_blank', 'noopener,noreferrer');
    setShowDropdown(false);
  };

  const toggleDropdown = (e) => {
    e.stopPropagation();
    if (!isExpired && virtualTours.length > 0) {
      // Check available space and determine if dropdown should open upwards
      if (!showDropdown && toggleRef.current) {
        const buttonRect = toggleRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        
        // Estimate dropdown height (each item ~56px + padding)
        const estimatedDropdownHeight = Math.min(virtualTours.length * 56 + 32, 400);
        
        const shouldOpenUpwards = spaceBelow < estimatedDropdownHeight && spaceAbove > estimatedDropdownHeight;

        const topPosition = shouldOpenUpwards
          ? buttonRect.top + window.scrollY - estimatedDropdownHeight - 8
          : buttonRect.bottom + window.scrollY + 8;

        setDropdownStyles({
          position: 'absolute',
          top: topPosition,
          left: buttonRect.left + window.scrollX,
          minWidth: Math.max(buttonRect.width, 280),
          maxHeight: estimatedDropdownHeight
        });
      }
      setShowDropdown(!showDropdown);
    } else if (!isExpired && virtualTours.length === 1) {
      // If only one tour, open it directly
      handleVirtualTourClick(e, virtualTours[0]);
    }
  };

  // Don't render if no tours available (and not loading)
  if (!isLoading && virtualTours.length === 0) {
    return null;
  }

  // Show loading state
  if (isLoading) {
    return (
      <button 
        className="action-link vtour"
        disabled={true}
        title="Loading virtual tours..."
      >
        <i className="fas fa-spinner fa-spin"></i>
        Loading...
      </button>
    );
  }

  // If only one tour, render as a simple button
  if (virtualTours.length === 1) {
    return (
      <button 
        className="action-link vtour"
        onClick={(e) => handleVirtualTourClick(e, virtualTours[0])}
        disabled={isExpired || isLoading}
        title={virtualTours[0].vtour_name}
      >
        <i className="fas fa-vr-cardboard"></i>
        {isLoading ? 'Loading...' : "Virtual Tour"}
      </button>
    );
  }

  // Render dropdown for multiple tours

  const renderDropdownMenu = () => (
    <div
      className="vtour-dropdown-menu"
      style={dropdownStyles}
      ref={menuRef}
    >
      {virtualTours.map((tour, index) => (
        <div 
          key={index}
          className="vtour-dropdown-item"
          onClick={(e) => handleVirtualTourClick(e, tour)}
        >
          <i className="fas fa-vr-cardboard"></i>
          <span className="vtour-name">{tour.vtour_name}</span>
          <span className="vtour-code">({tour.code})</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="vtour-dropdown-container" ref={dropdownRef}>
      <button 
        className={`action-link vtour vtour-dropdown-toggle ${showDropdown ? 'active' : ''}`}
        onClick={toggleDropdown}
        disabled={isExpired}
        title="Virtual Tours"
        ref={toggleRef}
      >
        <i className="fas fa-vr-cardboard"></i>
        {`Virtual Tours`}
        <i className={`fas fa-chevron-down dropdown-arrow ${showDropdown ? 'rotate' : ''}`}></i>
      </button>
      
      {showDropdown && !isExpired && (
        typeof document === 'undefined'
          ? renderDropdownMenu()
          : createPortal(renderDropdownMenu(), document.body)
      )}
    </div>
  );
};

export default ProjectCard;
